import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Linkedin, Instagram, Twitter, Facebook, Calendar, ArrowLeft, Mic, Search, ArrowRight } from 'lucide-react'
import CinematicNav, { BackToTop } from '../components/landing/CinematicNav'
import CinematicFooter from '../components/landing/CinematicFooter'
import Reveal from '../components/landing/Reveal'
import Breadcrumbs from '../components/masterclass/Breadcrumbs'
import SpeakerImage from '../components/masterclass/SpeakerImage'
import { resolveImageUrl } from '../utils/imageUrl'
import { SCHEDULE_SERIES_NAME } from '../utils/masterclassSchedule'
import { masterclassService } from '../services/masterclassService'

export default function MasterclassSpeakersPage() {
  const [scrolled, setScrolled] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [speakers, setSpeakers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    document.title = `Speaker Archive – ${SCHEDULE_SERIES_NAME} | Synergy Multimedia Academy`
    const handleScroll = () => {
      setScrolled(window.scrollY > 40)
      setShowBackToTop(window.scrollY > 800)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    masterclassService.getSpeakers()
      .then(d => setSpeakers(d.speakers || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const filtered = speakers.filter(s =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.company?.toLowerCase().includes(search.toLowerCase()) ||
    s.title?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="cinema-page min-h-screen">
      <div className="cinema-grain" aria-hidden="true" />

      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} onNavigate={() => {}} />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden bg-cinema-black">
        <div className="cinema-glow-orb w-[600px] h-[600px] -top-40 left-1/2 -translate-x-1/2 opacity-30" />
        <div className="cinema-container relative">
          <Reveal>
            <Breadcrumbs />
          </Reveal>
          <Reveal delay={100}>
            <span className="cinema-label block mt-10 mb-6">Speaker Archive</span>

            <h1 className="cinema-headline-xl">
              The Voices Behind<br />
              <span className="text-cinema-gold">The Creative Talk</span>
            </h1>

            <p className="cinema-body-lg mt-8 max-w-xl">
              Every speaker who has graced the {SCHEDULE_SERIES_NAME} stage — Uganda's top creatives, entrepreneurs and industry leaders.
            </p>
          </Reveal>

          {/* Search */}
          <Reveal delay={150}>
            <div className="mt-12 relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted" />
              <input
                type="text"
                placeholder="Search by name, company or expertise…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-4 bg-cinema-charcoal border border-white/8 text-white placeholder-cinema-muted/50 focus:outline-none focus:border-cinema-gold/40 transition-all font-sans text-sm"
                style={{ fontSize: 16 }}
              />
            </div>
          </Reveal>
        </div>
        <div className="cinema-divider mt-16" />
      </section>

      {/* Grid */}
      <section className="bg-cinema-charcoal py-24 sm:py-32">
        <div className="cinema-container">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-cinema-black/40 animate-pulse">
                  <div className="h-64 bg-white/4" />
                  <div className="p-8 space-y-4">
                    <div className="h-3 bg-white/5 w-2/3" />
                    <div className="h-5 bg-white/5 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-32">
              <div className="text-5xl mb-6">�</div>
              <h3 className="font-display text-white text-2xl font-light mb-3">{search ? 'No speakers found' : 'No speakers yet'}</h3>
              <p className="cinema-body text-sm">
                {search ? 'Try a different search term.' : 'Speakers will appear here once masterclasses are scheduled.'}
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 overflow-hidden">
              {filtered.map((speaker, i) => (
                <Reveal key={speaker.id} delay={i * 60}>
                  <SpeakerCard speaker={speaker} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CinematicFooter contactEmail="synergymultimediaacademy@gmail.com" />
      <BackToTop visible={showBackToTop} />
    </div>
  )
}

function SpeakerCard({ speaker }) {
  const [expanded, setExpanded] = useState(false)
  const socialLinks = [
    [speaker.linkedin_url, Linkedin, 'LinkedIn'],
    [speaker.instagram_url, Instagram, 'Instagram'],
    [speaker.twitter_url, Twitter, 'Twitter'],
    [speaker.facebook_url, Facebook, 'Facebook'],
  ].filter(([url]) => url)

  return (
    <div className="cinema-course-card group flex flex-col h-full">
      {/* Photo */}
      <div className="cinema-image-wrap aspect-[4/5] max-h-[22rem] overflow-hidden">
        {(() => {
          const hasPhoto = resolveImageUrl(speaker?.photo_url || speaker?.photo)
          return hasPhoto ? (
            <SpeakerImage speaker={speaker} loading="lazy" fit="card" />
          ) : (
            <div className="w-full h-full bg-cinema-graphite flex items-center justify-center">
              <Mic className="w-16 h-16 text-cinema-muted/20" />
            </div>
          )
        })()}
        <div className="cinema-course-overlay" />

        {speaker.sessions?.length > 0 && (
          <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-cinema-glow px-3 py-1.5">
            <Mic className="w-3 h-3 text-white" />
            <span className="cinema-label text-white text-xs font-semibold">
              {speaker.sessions.length} {speaker.sessions.length === 1 ? 'Session' : 'Sessions'}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-8 flex flex-col flex-1">
        <div className="flex-1">
          <h3 className="font-display text-white text-xl font-light">{speaker.name}</h3>
          {speaker.title && <p className="text-cinema-gold font-sans text-sm mt-2">{speaker.title}</p>}
          {speaker.company && <p className="text-cinema-muted font-sans text-xs mt-1">{speaker.company}</p>}

          {speaker.experience && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-cinema-gold text-xs">◆</span>
              <span className="cinema-label text-xs font-semibold">{speaker.experience}</span>
            </div>
          )}

          {speaker.bio && (
            <div className="mt-5">
              <p className={`cinema-body text-sm leading-relaxed ${!expanded ? 'line-clamp-3' : ''}`}>
                {speaker.bio}
              </p>
              {speaker.bio.length > 120 && (
                <button onClick={() => setExpanded(!expanded)} className="mt-2 cinema-btn-text text-xs">
                  {expanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
          )}

          {speaker.sessions?.length > 0 && (
            <div className="mt-6 space-y-3">
              {speaker.sessions.slice(0, expanded ? undefined : 2).map(s => (
                <div key={s.id} className="flex items-start gap-3">
                  <Calendar className="w-3.5 h-3.5 text-cinema-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-cinema-soft/70 font-sans text-xs leading-tight">{s.topic}</p>
                    <p className="text-cinema-muted font-sans text-xs mt-0.5">{new Date(s.event_date).toLocaleDateString('en-UG', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {socialLinks.length > 0 && (
          <div className="flex gap-2 mt-6 pt-6 border-t border-white/5">
            {socialLinks.map(([url, Icon, label]) => (
              <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                className="p-3 text-cinema-muted hover:text-cinema-gold transition-all duration-300"
                aria-label={label}>
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        )}

        <Link
          to={`/masterclass/speakers/${speaker.slug || speaker.id}`}
          className="mt-6 inline-flex items-center gap-2 text-cinema-gold text-sm font-sans font-medium hover:text-white transition-colors"
        >
          View Profile <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  )
}
